import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
// Relative import (not the '@/components' barrel) to avoid a cycle: the barrel exports
// Workbench, which renders this file.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip/Tooltip'
import { useIdeaNavigation, type UseIdeaNavigationOptions } from './ideaNavigation'

function NavButton({
  label,
  hint,
  disabled,
  onClick,
  children,
}: {
  label: string
  hint: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* A disabled button fires no pointer events, so the trigger wraps it. */}
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0">
            <button
              type="button"
              aria-label={label}
              disabled={disabled}
              onClick={onClick}
              className={cn(
                'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                disabled
                  ? 'text-text-disabled cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
              )}
            >
              {children}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** The toolbar's two arrows. Rendered by Workbench and slotted into the toolbar. */
export function WorkbenchIdeaNav(props: UseIdeaNavigationOptions) {
  const nav = useIdeaNavigation(props)
  const none = nav.count === 0
  return (
    <span className="inline-flex items-center shrink-0" data-testid="idea-nav">
      <NavButton
        label="Previous trade idea"
        hint={none ? 'No trade ideas on the chart' : 'Previous trade idea'}
        disabled={!nav.canPrev}
        onClick={nav.goPrev}
      >
        <ChevronLeft size={16} strokeWidth={1.75} />
      </NavButton>
      <NavButton
        label="Next trade idea"
        hint={none ? 'No trade ideas on the chart' : 'Next trade idea'}
        disabled={!nav.canNext}
        onClick={nav.goNext}
      >
        <ChevronRight size={16} strokeWidth={1.75} />
      </NavButton>
    </span>
  )
}
